import { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { PencilIcon, ChevronLeftIcon } from '@heroicons/react/outline';
import useAuthStore from '../../store/authStore';
import { getProfile, updateProfile } from '../../services/profileService';
import ProfileEdit from './ProfileEdit';

type LayoutContext = { openMenu: () => void };

const ProfilePage = () => {
  const { user, userData, setUserData } = useAuthStore();
  const { openMenu } = useOutletContext<LayoutContext>();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [profile, setProfile] = useState<any>({});
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfile = async () => {
      if (user) {
        try {
          setIsLoading(true);
          const profileData = await getProfile(user.uid);
          if (profileData) {
            setProfile(profileData);
          }
        } catch (error) {
          console.error('Error fetching profile:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchProfile();
  }, [user]);

  const handleSave = async (data: any) => {
    if (!user) return;
    
    try {
      await updateProfile(user.uid, data);
      // Update the user data in the auth store
      setUserData({ ...userData, ...data });
      setProfile({ ...profile, ...data });
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <span className="mr-2">جاري التحميل...</span>
      </div>
    );
  }

  if (isEditing) {
    return (
      <ProfileEdit 
        profile={profile}
        role={userData?.role || 'client'}
        onSave={handleSave}
        onCancel={() => setIsEditing(false)}
      />
    );
  }

  return (
    <div className="p-5 bg-gray-50 min-h-screen font-sans text-right dir-rtl mb-24">
      {/* Header */}
      <div className="flex items-center mb-6">
        <button 
          onClick={() => navigate(-1)} 
          className="p-2 rounded-full hover:bg-gray-200"
        >
          <ChevronLeftIcon className="h-5 w-5 text-gray-700 rotate-180" />
        </button>
        <h2 className="text-2xl font-bold text-gray-900 mr-4">الملف الشخصي</h2>
      </div>

      {/* Profile Section */}
      <div className="space-y-4">
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-2xl font-bold text-blue-600">
                  {userData?.fullName?.charAt(0) || 'م'}
                </span>
              </div>
              <div className="mr-4">
                <h2 className="text-lg font-bold text-gray-900">
                  {userData?.fullName || 'مستخدم'}
                </h2>
                <p className="text-sm text-gray-500">
                  {user?.email || 'بريد إلكتروني غير متوفر'}
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsEditing(true)}
              className="p-2 rounded-full bg-gray-50 text-gray-700 hover:bg-gray-200"
            >
              <PencilIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">
                {profile.contractsCount || '0'}
              </p>
              <p className="text-xs text-gray-500">العقود</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">
                {profile.completedContracts || '0'}
              </p>
              <p className="text-xs text-gray-500">مكتملة</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">
                {profile.rating || '5.0'}
              </p>
              <p className="text-xs text-gray-500">التقييم</p>
            </div>
          </div>
        </div>

        {/* Personal Information */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">المعلومات الشخصية</h3>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b border-gray-50">
              <span className="text-sm text-gray-500">الاسم الكامل</span>
              <span className="text-sm font-medium text-gray-900">
                {profile.fullName || 'غير محدد'}
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-50">
              <span className="text-sm text-gray-500">رقم الجوال</span>
              <span className="text-sm font-medium text-gray-900">
                {profile.phone || 'غير محدد'}
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-50">
              <span className="text-sm text-gray-500">البريد الإلكتروني</span>
              <span className="text-sm font-medium text-gray-900">
                {user?.email || 'غير محدد'}
              </span>
            </div>
            {userData?.role === 'client' ? (
              <>
                <div className="flex justify-between items-center py-2 border-b border-gray-50">
                  <span className="text-sm text-gray-500">رقم الهوية</span>
                  <span className="text-sm font-medium text-gray-900">
                    {profile.nationalId || 'غير محدد'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-50">
                  <span className="text-sm text-gray-500">الوظيفة</span>
                  <span className="text-sm font-medium text-gray-900">
                    {profile.jobTitle || 'غير محدد'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-50">
                  <span className="text-sm text-gray-500">الراتب الشهري</span>
                  <span className="text-sm font-medium text-gray-900">
                    {profile.monthlyIncome ? `${profile.monthlyIncome} ر.س` : 'غير محدد'}
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="flex justify-between items-center py-2 border-b border-gray-50">
                  <span className="text-sm text-gray-500">رقم الرخصة</span>
                  <span className="text-sm font-medium text-gray-900">
                    {profile.licenseNumber || 'غير محدد'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-50">
                  <span className="text-sm text-gray-500">رقم السجل التجاري</span>
                  <span className="text-sm font-medium text-gray-900">
                    {profile.commercialRegistryNumber || 'غير محدد'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-sm text-gray-500">موقع المكتب</span>
                  <span className="text-sm font-medium text-gray-900">
                    {profile.officeLocation || 'غير محدد'}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Account Information */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">معلومات الحساب</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b border-gray-50">
              <span className="text-sm text-gray-500">رقم الحساب البنكي (IBAN)</span>
              <span className="text-sm font-medium text-gray-900">
                {profile.iban || 'غير محدد'}
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-50">
              <span className="text-sm text-gray-500">تاريخ الانضمام</span>
              <span className="text-sm font-medium text-gray-900">
                {new Date(profile.createdAt || new Date()).toLocaleDateString('en-SA')}
              </span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-gray-500">نوع الحساب</span>
              <span className="text-sm font-medium text-gray-900">
                {userData?.role === 'client' ? 'عميل' : 'محامي'}
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-2">
          <button className="w-full py-3 px-4 bg-white rounded-xl text-right font-medium text-gray-900 border border-gray-200 hover:bg-gray-50">
            الشروط والأحكام
          </button>
          <button className="w-full py-3 px-4 bg-white rounded-xl text-right font-medium text-gray-900 border border-gray-200 hover:bg-gray-50">
            سياسة الخصوصية
          </button>
          <button 
            onClick={async () => {
              await useAuthStore.getState().logout();
              navigate('/login');
            }}
            className="w-full py-3 px-4 bg-red-50 rounded-xl text-right font-medium text-red-600 hover:bg-red-100"
          >
            تسجيل الخروج
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
